import { useState, useMemo, useCallback } from "react";
import { trpc } from "../../trpc";
import { Merchant } from "../../types";
import SectionTitle from "../../components/SectionTitle";
import MerchantCard from "../../components/merchants/MerchantCard";
import EditMerchantModal from "../../components/merchants/EditMerchantModal";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Search, Users, Filter, AlertCircle, Loader2, RefreshCw, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

type FilterType = "all" | "incomplete" | "has-emails" | "no-emails";
type TemplateFilterType = "all" | "none" | "pending" | "approved" | "rejected" | "deleted";

const BULK_DELAY_MS = 2000;

const MerchantsContainer = () => {
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const [templateFilter, setTemplateFilter] = useState<TemplateFilterType>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [bulkSyncOpen, setBulkSyncOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; merchantId: string } | null>(null);

  const merchantsQuery = trpc.merchant.merchants.useQuery();

  const { data: merchants, isLoading, error, isError, refetch } = merchantsQuery;

  const rentalsMerchantIds = useMemo(
    () => (merchants ?? []).filter((m) => m.type === "rentals").map((m) => m.id),
    [merchants]
  );

  const templateStatusQuery = trpc.whatsappTemplates.bulkEmailTemplateStatus.useQuery(
    { merchantIds: rentalsMerchantIds },
    { enabled: rentalsMerchantIds.length > 0 }
  );

  const autoCreateMutation = trpc.whatsappTemplates.autoCreateEmailTemplates.useMutation();
  const syncMutation = trpc.whatsappTemplates.syncStatuses.useMutation();

  const handleBulkCreate = useCallback(async () => {
    const total = rentalsMerchantIds.length;
    let created = 0;
    let errors = 0;

    for (let i = 0; i < total; i++) {
      const merchantId = rentalsMerchantIds[i];
      setBulkProgress({ current: i + 1, total, merchantId });

      try {
        await autoCreateMutation.mutateAsync({ merchantId });
        created++;
      } catch (err) {
        errors++;
        console.error(`Error creating templates for ${merchantId}:`, err);
      }

      if (i < total - 1) {
        await new Promise((resolve) => setTimeout(resolve, BULK_DELAY_MS));
      }
    }

    setBulkProgress(null);
    templateStatusQuery.refetch();

    if (errors > 0) {
      toast.warning(`Plantillas creadas para ${created}/${total} merchants (${errors} errores)`);
    } else {
      toast.success(`Plantillas creadas para ${created} merchants`);
    }
  }, [rentalsMerchantIds, autoCreateMutation, templateStatusQuery]);

  const handleBulkSync = useCallback(async () => {
    const total = rentalsMerchantIds.length;
    let synced = 0;
    let errors = 0;

    for (let i = 0; i < total; i++) {
      const merchantId = rentalsMerchantIds[i];
      setBulkProgress({ current: i + 1, total, merchantId });

      try {
        await syncMutation.mutateAsync({ merchantId });
        synced++;
      } catch (err) {
        errors++;
        console.error(`Error syncing templates for ${merchantId}:`, err);
      }

      if (i < total - 1) {
        await new Promise((resolve) => setTimeout(resolve, BULK_DELAY_MS));
      }
    }

    setBulkProgress(null);
    templateStatusQuery.refetch();

    if (errors > 0) {
      toast.warning(`Sincronizados ${synced}/${total} merchants (${errors} errores)`);
    } else {
      toast.success(`Sincronizados ${synced} merchants`);
    }
  }, [rentalsMerchantIds, syncMutation, templateStatusQuery]);

  const isMerchantIncomplete = (merchant: Merchant): boolean => {
    if (merchant.currentCustomer) {
      return !merchant.firstInvoicePaidDate || merchant.minimumSubscriptionUsage === undefined || merchant.minimumSubscriptionUsage === null;
    }
    return !merchant.firstInvoicePaidDate || !merchant.lastInvoicePaidDate;
  };

  const filteredMerchants = useMemo(() => {
    if (!merchants) return [];

    let result = merchants;

    if (filter === "incomplete") {
      result = result.filter((merchant) => isMerchantIncomplete(merchant));
    } else if (filter === "has-emails") {
      result = result.filter((merchant) => (merchant.merchantIdentifierEmail?.length ?? 0) > 0);
    } else if (filter === "no-emails") {
      result = result.filter((merchant) => !merchant.merchantIdentifierEmail?.length);
    }

    if (templateFilter !== "all" && templateStatusQuery.data) {
      result = result.filter((merchant) => {
        const status = templateStatusQuery.data[merchant.id];
        if (templateFilter === "none") return !status || status === "none";
        return status === templateFilter;
      });
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((merchant) =>
        merchant.id.toLowerCase().includes(term) ||
        merchant.merchantIdentifierEmail?.some(email =>
          email.toLowerCase().includes(term)
        )
      );
    }

    return result;
  }, [merchants, filter, templateFilter, templateStatusQuery.data, searchTerm]);

  const handleEditMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedMerchant(null);
  };

  const handleUpdateSuccess = () => {
    refetch();
  };

  const isBulkRunning = bulkProgress !== null;

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Merchants"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Merchants" }
        ]}
        description="Gestiona los merchants y sus configuraciones"
      />

      {!isLoading && merchants && merchants.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
                <div className="space-y-2 flex-1 min-w-[160px]">
                  <Label htmlFor="filter">Filtro</Label>
                  <Select value={filter} onValueChange={(value) => setFilter(value as FilterType)}>
                    <SelectTrigger id="filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="incomplete">Incompletos</SelectItem>
                      <SelectItem value="has-emails">Con emails</SelectItem>
                      <SelectItem value="no-emails">Sin emails</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex-1 min-w-[160px]">
                  <Label htmlFor="templateFilter">Plantillas</Label>
                  <Select value={templateFilter} onValueChange={(value) => setTemplateFilter(value as TemplateFilterType)}>
                    <SelectTrigger id="templateFilter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="none">Sin plantillas</SelectItem>
                      <SelectItem value="pending">Pendientes</SelectItem>
                      <SelectItem value="approved">Aprobadas</SelectItem>
                      <SelectItem value="rejected">Rechazadas</SelectItem>
                      <SelectItem value="deleted">Eliminadas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex-[2] min-w-[300px]">
                  <Label htmlFor="search">Buscar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="search"
                      placeholder="Buscar por ID o email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {rentalsMerchantIds.length > 0 && (
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones masivas
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setBulkCreateOpen(true)}
                    disabled={isBulkRunning}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Crear plantillas para todos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setBulkSyncOpen(true)}
                    disabled={isBulkRunning}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Sincronizar todos
                  </Button>
                  {bulkProgress && (
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {bulkProgress.current}/{bulkProgress.total} — {bulkProgress.merchantId}
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Spinner className="w-8 h-8" />
        </div>
      )}

      {isError && (
        <Alert status="error" className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Error loading merchants</p>
            <p className="text-sm">{error?.message || "Unknown error occurred"}</p>
          </div>
        </Alert>
      )}

      {merchants && merchants.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{filteredMerchants.length}</span> of{" "}
            <span className="font-semibold">{merchants.length}</span> merchants
          </p>

          <div className="flex flex-col gap-4">
            {filteredMerchants.map((merchant) => (
              <MerchantCard
                key={merchant.id}
                merchant={merchant}
                onEdit={handleEditMerchant}
                isIncomplete={isMerchantIncomplete(merchant)}
              />
            ))}
          </div>
        </div>
      )}

      {merchants && merchants.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-gray-900">No merchants found</p>
              <p className="text-sm text-gray-500">Get started by adding your first merchant</p>
            </div>
          </CardContent>
        </Card>
      )}

      {merchants && merchants.length > 0 && filteredMerchants.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              {searchTerm ? (
                <Search className="w-8 h-8 text-gray-400" />
              ) : (
                <Filter className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-gray-900">
                {searchTerm ? "No merchants match your search" : "No merchants match the selected filter"}
              </p>
              <p className="text-sm text-gray-500">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "Try selecting a different filter option"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <EditMerchantModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        merchant={selectedMerchant}
        onSuccess={handleUpdateSuccess}
      />

      <AlertDialog open={bulkCreateOpen} onOpenChange={setBulkCreateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Crear plantillas para todos los merchants</AlertDialogTitle>
            <AlertDialogDescription>
              Esto creara las plantillas de WhatsApp para los{" "}
              <span className="font-semibold">{rentalsMerchantIds.length}</span> merchants de
              tipo rentals. Las plantillas existentes se omitiran. Se procesara un merchant cada{" "}
              {BULK_DELAY_MS / 1000} segundos para evitar limites de Meta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                handleBulkCreate();
              }}
            >
              Crear para todos ({rentalsMerchantIds.length})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkSyncOpen} onOpenChange={setBulkSyncOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sincronizar estados de plantillas</AlertDialogTitle>
            <AlertDialogDescription>
              Esto sincronizara el estado de las plantillas de WhatsApp con Meta para los{" "}
              <span className="font-semibold">{rentalsMerchantIds.length}</span> merchants de
              tipo rentals. Se procesara un merchant cada {BULK_DELAY_MS / 1000} segundos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleBulkSync();
              }}
            >
              Sincronizar todos ({rentalsMerchantIds.length})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MerchantsContainer;
